import type { PlayerProfile } from './types';

/**
 * Whether a profile holds anything a player would miss.
 *
 * Deliberately not "is this profile different from a blank one": the question this
 * answers is whether replacing the copy would take something away. Cards, captured
 * Garaimon and completed battles are the three things that only ever accumulate, so
 * any of them means there is something to lose. The chosen region is not counted: it
 * is a setting with a default, so a player who has only ever picked a region has not
 * yet done anything that cannot be redone in one tap.
 */
export function profileHoldsProgress(profile: PlayerProfile): boolean {
  return profile.episodes.length > 0 || profile.garaimon.length > 0 || profile.gachaCards > 0;
}

/**
 * What to do about the device's save when an account signs in.
 *
 * The device keeps one save slot, shared by everyone who plays on it, while the
 * account keeps its own. When both hold progress, using one discards the other, so
 * the case that matters is the one where that choice is real. This decides which
 * cases need the player to choose, which have a single correct answer, and which
 * cannot be answered yet.
 *
 * The order of the rules is the whole design, because more than one can apply:
 *
 * 1. Nothing on the device: the account is the only copy, so it is used. Without a
 *    readable account there is nothing to open at all, which is why this is the one
 *    case that stops rather than continuing on the device.
 * 2. The device copy is stamped as already in this account: the two agree, so the
 *    account is used and no question is asked. This is the ordinary signed-in
 *    start-up, including one with no connection - the device copy *is* the account's
 *    copy, so nothing needs reading.
 * 3. The device copy is stamped as another account's: it is someone else's, so it is
 *    never offered up as this account's to overwrite. This rule comes before the
 *    empty-account rule on purpose: without it, signing into a fresh account on a
 *    shared device would offer to push the previous player's progress into it.
 * 4. Nothing in the account: there is no second copy to weigh, so the device's
 *    progress is kept and written up. This is a new account, which is the common case
 *    for a player who has been playing without one.
 * 5. Otherwise both copies hold progress and the device copy is not known to be in
 *    the account, so the player chooses.
 *
 * "Cannot be read" is kept separate from "holds nothing" throughout. Treating an
 * unreachable account as an empty one is how a newer copy gets written over an older
 * one with nobody asked, which is the fault this exists to prevent.
 */
export type SignInSaveResolution =
  /** Use the account as it stands. The device copy is replaced. */
  | 'load-account'
  /** The account holds nothing, so the device's progress is kept and written up. */
  | 'keep-device'
  /** Both hold progress. The player chooses which to keep. */
  | 'ask'
  /** The device copy belongs to a different account, so it is dropped. */
  | 'discard-device'
  /** Nothing is decided or written: the device copy is played, the account untouched. */
  | 'hold'
  /** The account is needed and cannot be read. Nothing is opened, and no copy is touched. */
  | 'unreachable';

export interface SignInSaveInput {
  /** Whether the device's copy holds anything worth keeping. */
  deviceHoldsProgress: boolean;
  /** Whether the account could not be read. */
  accountUnreadable: boolean;
  /** Whether the account's own copy holds progress. Meaningless when unreadable. */
  accountHoldsProgress: boolean;  /** The account this device's copy is stamped into, or null when unknown. */
  syncedAccountId: string | null;
  /** The account signing in. */
  accountId: string;
}

export function resolveSignInSave(input: SignInSaveInput): SignInSaveResolution {
  if (!input.deviceHoldsProgress) {
    // Rule 1. There is nothing local to protect, so the account is wanted either way.
    return input.accountUnreadable ? 'unreachable' : 'load-account';
  }
  if (input.syncedAccountId === input.accountId) {
    // Rule 2. Already reconciled with this account, so there is no choice to make.
    return 'load-account';
  }
  if (input.syncedAccountId !== null) {
    // Rule 3. Someone else's copy. It is not this account's to take on, and reading
    // the account is required before anything can be opened.
    return input.accountUnreadable ? 'unreachable' : 'discard-device';
  }
  if (input.accountUnreadable) {
    // Rule 5's case without the information to resolve it. The device copy is playable
    // and nothing is written, so playing on cannot cost the account anything.
    return 'hold';
  }
  if (!input.accountHoldsProgress) {
    // Rule 4. A new account: keep playing and write the progress up.
    return 'keep-device';
  }
  // Rule 5. A real choice, and the only case where the player is asked.
  return 'ask';
}
